import React from 'react';

import useForceUpdate from './use-force-update';
import useLayoutEffect from './use-isomorphic-layout-effect';

import type { SlotNames, Slots, UseSlotsProps, UseSlotsPropsReturn } from '../types/slots.types';

const useSlots = ({ slotNames }: UseSlotsProps): UseSlotsPropsReturn => {
  const slotsRef = React.useRef<Slots>(Object.fromEntries(slotNames.map((name) => [name, null])));

  const rerenderWithSlots = useForceUpdate();
  const [isMounted, setIsMounted] = React.useState(false);

  useLayoutEffect(() => {
    rerenderWithSlots();
    setIsMounted(true);
  }, [rerenderWithSlots]);

  const registerSlot = React.useCallback(
    (name: SlotNames, contents: React.ReactNode) => {
      if (!slotNames.includes(name)) {
        return;
      }

      slotsRef.current[name] = contents;
      if (isMounted) {
        rerenderWithSlots();
      }
    },
    [isMounted, rerenderWithSlots, slotNames],
  );

  const unregisterSlot = React.useCallback(
    (name: SlotNames) => {
      if (!slotNames.includes(name)) {
        return;
      }
      slotsRef.current[name] = null;
      rerenderWithSlots();
    },
    [rerenderWithSlots, slotNames],
  );

  const contextValue = React.useMemo(() => ({ registerSlot, unregisterSlot }), [registerSlot, unregisterSlot]);

  return { contextValue, slots: slotsRef.current };
};

export default useSlots;
